import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  CategoryIcon,
  EmptyState,
  FilterChip,
  ProductCard,
  Skeleton,
  pickText,
} from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { useSettings } from '../app/settings.js';
import { api } from '../lib/api.js';
import { useAddToCart } from '../lib/use-add-to-cart.js';

export function CatalogScreen({
  categoryId,
  title,
}: {
  categoryId?: string;
  title?: string;
}): ReactNode {
  const { t, locale } = useSettings();
  const { push, replace } = useNavigation();
  const cart = useAddToCart();
  // Kategoriya tanlanmagan "Katalog"da mijoz kategoriyalarga shu yerdan o'tadi:
  // faqat tovarlar ro'yxati ko'rsatilsa, "qanday bo'limlar bor" degan savolga javob yo'q.
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: api.categories,
  });
  const browsable = (categories.data ?? []).filter((category) => category.productCount !== 0);
  const [sort, setSort] = useState<'popular' | 'price_asc' | 'price_desc'>('popular');

  /*
   * Sahifalab yuklanadi. Ilgari faqat BIRINCHI 24 ta olinardi va qolganini ko'rishning
   * yo'li yo'q edi ("Filtrlar: 112" deyiladi, lekin 24 tasi chiqardi). `nextCursor`
   * serverdan keladi; tugasa `null`.
   */
  const products = useInfiniteQuery({
    queryKey: ['products', categoryId ?? 'all', sort],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.products({
        ...(categoryId === undefined ? {} : { categoryId }),
        ...(pageParam === undefined ? {} : { cursor: pageParam }),
        sort,
        limit: 24,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = products.data?.pages.flatMap((page) => page.items) ?? [];
  const total = products.data?.pages[0]?.total ?? 0;

  // Ro'yxat oxiriga yetganda keyingi sahifa o'zi yuklanadi; tugma esa zaxira
  // (IntersectionObserver bo'lmagan yoki ishlamagan muhit uchun).
  const sentinel = useRef<HTMLDivElement | null>(null);

  // Tanlangan kategoriya chiplar qatorida ko'rinib turishi kerak: qator uzun, mijoz keyingisini shu yerdan tanlaydi.
  const chips = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const row = chips.current;
    if (row === null) return;
    const active = row.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (active === null) return;
    row.scrollTo({
      left: active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2,
      behavior: 'smooth',
    });
  }, [categoryId, browsable.length]);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = products;
  useEffect(() => {
    const node = sentinel.current;
    if (node === null || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

  const sorts = [
    { id: 'popular', label: t('home.popular') },
    { id: 'price_asc', label: t('catalog.sortAsc') },
    { id: 'price_desc', label: t('catalog.sortDesc') },
  ] as const;

  return (
    <div className="aps-screen">
      <header className="aps-screen__header">
        <h1 className="aps-screen__title">{title ?? t('catalog.title')}</h1>
      </header>

      {browsable.length > 0 && (
        <div
          className="aps-scroll-x aps-sticky-chips"
          role="group"
          aria-label={t('categories.title')}
          ref={chips}
        >
          <FilterChip
            label={t('categories.all')}
            active={categoryId === undefined}
            onToggle={() => {
              replace({ name: 'catalog' });
            }}
          />
          {browsable.map((category) => (
            <FilterChip
              key={category.id}
              label={pickText(category.nameUz, category.nameRu, locale)}
              active={category.id === categoryId}
              onToggle={() => {
                replace({
                  name: 'catalog',
                  categoryId: category.id,
                  title: pickText(category.nameUz, category.nameRu, locale),
                });
              }}
            />
          ))}
        </div>
      )}

      <div className="aps-scroll-x">
        {sorts.map((option) => (
          <FilterChip
            key={option.id}
            label={option.label}
            active={sort === option.id}
            onToggle={() => {
              setSort(option.id);
            }}
          />
        ))}
      </div>

      {products.isPending ? (
        <div className="aps-grid-2">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} height="220px" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState title={t('catalog.empty')} />
      ) : (
        <>
          <div className="aps-grid-2">
            {items.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                locale={locale}
                fallbackIcon={<CategoryIcon iconKey={product.categoryIconKey} size={50} />}
                onAddToCart={cart.add}
                addToCartLabel={t('card.addToCart')}
                addToCartDisabled={cart.disabled}
                onSelect={() => {
                  push({ name: 'product', slug: product.slug });
                }}
              />
            ))}
          </div>

          <div ref={sentinel} aria-hidden="true" />
          {hasNextPage ? (
            <Button
              block
              variant="secondary"
              disabled={isFetchingNextPage}
              onClick={() => {
                void fetchNextPage();
              }}
            >
              {`${t('catalog.more')} (${String(items.length)} / ${String(total)})`}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
